import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchDepartments,
  updateDepartment,
} from "@/services/frontendServices/departmentsServices";
import { showErrorToast } from "@/lib/error-handling";
import {
  createDepartment,
  CreateDepartmentData,
} from "@/services/frontendServices/departmentsServices";
import { DepartmentDto } from "@/types/department-schema";
import { UpdateDepartmentInput } from "@/schemas";
import { usersKeys } from "@/hooks/queries";

/**
 * Custom hook to fetch departments for the current organization
 * @returns Query result with departments data
 */
export const useDepartments = () => {
  return useQuery<DepartmentDto[], Error>({
    queryKey: ["departments"],
    queryFn: fetchDepartments,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Custom hook to create a new department
 * @returns Mutation result for creating departments
 */
export const useCreateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation<DepartmentDto, Error, CreateDepartmentData>({
    mutationFn: createDepartment,
    onSuccess: (data) => {
      // Invalidate and refetch departments list
      queryClient.invalidateQueries({ queryKey: ["departments"] });

      toast.success("Department created successfully", {
        description: `${data.name} has been created.`,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating department",
        defaultMessage: "Failed to create department",
        duration: 5000,
      });
    },
  });
};

/**
 * Hook for updating departments with partial data
 * Only available for admin users
 */
export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DepartmentDto,
    Error,
    UpdateDepartmentInput & { id: string }
  >({
    mutationFn: async (data: UpdateDepartmentInput & { id: string }) => {
      const { id, ...updateData } = data;

      return await updateDepartment(id, updateData);
    },
    onSuccess: () => {
      // Invalidate and refetch departments list
      queryClient.invalidateQueries({ queryKey: ["departments"] });

      // Invalidate users list since department members changed
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error updating department",
        defaultMessage: "Failed to update department",
        duration: 5000,
      });
    },
  });
};
